import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteEmailCampaign,
  deleteEmailCampaignAttachment,
  downloadEmailCampaignAttachment,
  duplicateEmailCampaign,
  getEmailCampaign,
  listEmailCampaignDeliveries,
  mapLaravelError,
} from '../../services/emailCampaignsApi';
import EmailCampaignStatusChip from './components/EmailCampaignStatusChip';
import EmailCampaignDeliveriesTable from './components/EmailCampaignDeliveriesTable';
import recurrenceSummary from './utils/recurrenceSummary';
import EmailCampaignTourDialog from './components/EmailCampaignTourDialog';

const DETAILS_TOUR_STORAGE_KEY = 'email_campaigns_tour_details_v1';

const DETAILS_TOUR_STEPS = [
  {
    title: 'Campaign Summary',
    description: 'Check status, recipients, and schedule at the top before taking any action.',
  },
  {
    title: 'Content Preview',
    description: 'Use the Content tab to verify the message body and bilingual output before sending future copies.',
  },
  {
    title: 'Deliveries and Attachments',
    description: 'Review delivery records and manage attachments from dedicated tabs.',
  },
];

export default function EmailCampaignDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const campaignId = Number(id || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState(null);

  const [tab, setTab] = useState('deliveries');
  const [tourOpen, setTourOpen] = useState(false);

  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryMeta, setDeliveryMeta] = useState({ current_page: 1, per_page: 25, total: 0 });

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const payload = await getEmailCampaign(campaignId);
      setCampaign(payload?.data || null);
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const loadDeliveries = useCallback(async (page = 1, perPage = 25) => {
    setDeliveriesLoading(true);

    try {
      const payload = await listEmailCampaignDeliveries(campaignId, {
        page,
        per_page: perPage,
      });

      setDeliveries(Array.isArray(payload?.data) ? payload.data : []);
      setDeliveryMeta(payload?.meta || { current_page: page, per_page: perPage, total: 0 });
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setDeliveriesLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (tab === 'deliveries') {
      loadDeliveries(deliveryMeta.current_page || 1, deliveryMeta.per_page || 25);
    }
  }, [deliveryMeta.current_page, deliveryMeta.per_page, loadDeliveries, tab]);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(DETAILS_TOUR_STORAGE_KEY);
      if (!seen) {
        setTourOpen(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const readOnly = useMemo(() => ['sending', 'sent'].includes(String(campaign?.status || '').toLowerCase()), [campaign?.status]);

  const handleDeleteAttachment = async (attachmentId) => {
    if (!campaign) return;

    try {
      await deleteEmailCampaignAttachment(campaign.id, attachmentId);
      await loadCampaign();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      const payload = await downloadEmailCampaignAttachment(campaignId, attachmentId);
      const url = window.URL.createObjectURL(payload.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = payload.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDuplicate = async () => {
    try {
      const payload = await duplicateEmailCampaign(campaignId);
      const nextId = payload?.campaign?.data?.id;
      if (nextId) {
        navigate(`/admin/communications/email-campaigns/${nextId}/edit`);
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(`Delete "${campaign?.title || 'this campaign'}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteEmailCampaign(campaignId);
      navigate('/admin/communications/email-campaigns');
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const finishTour = () => {
    // Once the tour is closed (any way), remember it so it never auto-pops again.
    try {
      window.localStorage.setItem(DETAILS_TOUR_STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setTourOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Campaign not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.75}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {campaign.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailCampaignStatusChip status={campaign.status} />
              <Chip label={`${Number(campaign.recipient_snapshot_count || 0)} recipients`} size="small" />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<HelpOutlineOutlinedIcon />} onClick={() => setTourOpen(true)}>
              Tour
            </Button>
            <Button onClick={() => navigate(`/admin/communications/email-campaigns/${campaign.id}/edit`)} disabled={readOnly}>
              Edit
            </Button>
            <Button variant="outlined" startIcon={<ContentCopyOutlinedIcon />} onClick={handleDuplicate}>
              Duplicate
            </Button>
            <Button
              color="error"
              startIcon={<DeleteOutlineOutlinedIcon />}
              onClick={handleDelete}
              disabled={!campaign.can_delete}
            >
              Delete
            </Button>
            <Button onClick={() => navigate('/admin/communications/email-campaigns')}>
              Back
            </Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <Alert severity="info">
          Tip: duplicate this campaign to reuse content, audience rules, and attachments for future communications.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Schedule</Typography>
            <Typography variant="body2">{recurrenceSummary(campaign)}</Typography>

            <Divider />

            <Typography variant="subtitle2">Subject</Typography>
            <Typography variant="body2">{campaign.subject || '-'}</Typography>

            <Divider />

            <Typography variant="subtitle2">Delivery Stats</Typography>
            <Typography variant="body2">
              Sent: {Number(campaign?.delivery_stats?.sent || 0)} | Failed: {Number(campaign?.delivery_stats?.failed || 0)} |
              Queued: {Number(campaign?.delivery_stats?.queued || 0)}
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="deliveries" label="Deliveries" />
            <Tab value="content" label="Content" />
            <Tab value="attachments" label="Attachments" />
            <Tab value="settings" label="Settings" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === 'deliveries' ? (
              <EmailCampaignDeliveriesTable
                rows={deliveries}
                loading={deliveriesLoading}
                page={deliveryMeta.current_page || 1}
                perPage={deliveryMeta.per_page || 25}
                total={deliveryMeta.total || 0}
                onPageChange={(nextPage) =>
                  setDeliveryMeta((prev) => ({
                    ...prev,
                    current_page: nextPage,
                  }))
                }
                onPerPageChange={(nextPerPage) =>
                  setDeliveryMeta((prev) => ({
                    ...prev,
                    per_page: nextPerPage,
                    current_page: 1,
                  }))
                }
              />
            ) : null}

            {tab === 'content' ? (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Subject</Typography>
                <Typography variant="body2">{campaign.subject || '-'}</Typography>
                <Typography variant="subtitle2">Preheader</Typography>
                <Typography variant="body2">{campaign.preheader || '-'}</Typography>

                <Divider />

                {campaign.is_bilingual ? (
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2">English Content</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box dangerouslySetInnerHTML={{ __html: campaign.html_body_en || campaign.html_body || '' }} />
                    </Paper>
                    <Typography variant="subtitle2">Arabic Content</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box
                        sx={{ direction: 'rtl', textAlign: 'right' }}
                        dangerouslySetInnerHTML={{ __html: campaign.html_body_ar || '' }}
                      />
                    </Paper>
                  </Stack>
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Box dangerouslySetInnerHTML={{ __html: campaign.html_body || '' }} />
                  </Paper>
                )}
              </Stack>
            ) : null}

            {tab === 'attachments' ? (
              <Stack spacing={1.5}>
                {(campaign.attachments || []).map((attachment) => (
                  <Paper key={attachment.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {attachment.original_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {attachment.mime || '-'} | {Number(attachment.size_bytes || 0)} bytes
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={0.5}>
                        <IconButton onClick={() => handleDownloadAttachment(attachment.id)}>
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>

                        {!readOnly ? (
                          <IconButton color="error" onClick={() => handleDeleteAttachment(attachment.id)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}

                {(campaign.attachments || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No attachments.
                  </Typography>
                ) : null}
              </Stack>
            ) : null}

            {tab === 'settings' ? (
              <Stack spacing={0.75}>
                <Typography variant="body2">From Name: {campaign.from_name || '-'}</Typography>
                <Typography variant="body2">From Email: {campaign.from_email || '-'}</Typography>
                <Typography variant="body2">Reply-To: {campaign.reply_to || '-'}</Typography>
                <Typography variant="body2">
                  In-app notification: {campaign.send_in_app_notification ? 'Enabled' : 'Disabled'}
                </Typography>
                <Typography variant="body2">Timezone: {campaign.timezone || '-'}</Typography>
                <Typography variant="body2">Audience mode: {campaign.audience_mode || '-'}</Typography>
                <Typography variant="body2">Status: {campaign.status}</Typography>
              </Stack>
            ) : null}
          </Box>
        </Paper>
      </Stack>

      <EmailCampaignTourDialog
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onDismiss={finishTour}
        onFinish={finishTour}
        title="Campaign Details Tour"
        steps={DETAILS_TOUR_STEPS}
      />
    </Box>
  );
}

